// src/OrganizationManagementModal.js
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import "./OrganizationManagementModal.css";
import logger from "./utils/logger";
import OrgUsersModal from "./OrgUsersModal";
import StatusModal from "./components/OrganizationManagement/StatusModal";
import ConfirmationDialog from "./components/OrganizationManagement/ConfirmationDialog";
import OrganizationForm from "./components/OrganizationManagement/OrganizationForm";
import OrganizationTable from "./components/OrganizationManagement/OrganizationTable";
import usePermissions from "./hooks/usePermissions";
import useOrganizationAPI from "./hooks/useOrganizationAPI";
import useOrganizationForm from "./hooks/useOrganizationForm";
import {
  useEscapeKey,
  useEnterKeyForModal,
} from "./hooks/useModalKeyboardHandlers";

const OrganizationManagementModal = ({
  isOpen,
  onClose,
  theme,
  organizations = [],
  currentOrganizationId,
  userProfile,
  getAccessToken,
  onOrganizationsChanged,
  onSwitchOrganization,
  onChangeOrganization, // Function to trigger the change organization screen
  onOpenInstallationManagement, // Function to open Installation Management for a specific org
}) => {
  // Permissions hook
  const permissions = usePermissions(userProfile);

  // Organization API hook
  const {
    createOrganization,
    updateOrganization,
    deleteOrganization,
    authorizeOrganization,
    unauthorizeOrganization,
    fetchOrganizationSettings,
    creationModal,
    updateModal,
    deletionModal,
    authorizationModal,
    setCreationModal,
    setUpdateModal,
    setDeletionModal,
    setAuthorizationModal,
  } = useOrganizationAPI(getAccessToken, onOrganizationsChanged, userProfile);

  // Organization form hook
  const {
    showCreateForm,
    editingOrganization,
    formData,
    setFormData,
    startCreate,
    startEdit,
    cancelForm,
    resetForm,
    validateForm,
  } = useOrganizationForm(organizations);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [pendingSwitch, setPendingSwitch] = useState(null); // { orgId, name }
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // { organization, installationCount }
  const [deauthorizeConfirmation, setDeauthorizeConfirmation] = useState(null); // { organization }
  const [searchTerm, setSearchTerm] = useState("");
  const [showOrgUsersModal, setShowOrgUsersModal] = useState(false);
  const [selectedOrgForUsers, setSelectedOrgForUsers] = useState(null);
  const [copiedOrgId, setCopiedOrgId] = useState(null);
  const prevIsOpenRef = useRef(isOpen);

  // Track if isOpen prop changes
  useLayoutEffect(() => {
    if (prevIsOpenRef.current !== isOpen) {
      logger.log("[OrgManagementModal isOpen changed]", {
        from: prevIsOpenRef.current,
        to: isOpen,
      });
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen]);

  // Handle copy organization ID
  const handleCopyOrgId = async (orgId) => {
    try {
      await navigator.clipboard.writeText(orgId);
      setCopiedOrgId(orgId);
      setTimeout(() => setCopiedOrgId(null), 2000);
    } catch (err) {
      logger.error("Failed to copy organization ID:", err);
    }
  };

  // Form handlers (must be defined before useEscapeKey)
  const handleCancelForm = () => {
    cancelForm();
    setMessage(null);
  };

  // ESC key handler
  useEscapeKey(isOpen, loading, onClose, {
    showOrgUsersModal,
    deleteConfirmation,
    deauthorizeConfirmation,
    showCreateForm,
    editingOrganization,
    onCancelForm: handleCancelForm,
    onClearDeleteConfirmation: () => setDeleteConfirmation(null),
    onClearDeauthorizeConfirmation: () => setDeauthorizeConfirmation(null),
  });

  // Enter key handler for authorization modal
  useEnterKeyForModal(authorizationModal, setAuthorizationModal);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      resetForm();
      setMessage(null);
      setPendingSwitch(null);
      setDeauthorizeConfirmation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    logger.log("[handleClose] Called", { loading });
    if (!loading) {
      logger.log("[handleClose] Calling parent onClose()");
      onClose();
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCreateClick = () => {
    if (!permissions.canCreateOrganization) {
      showMessage("error", permissions.getPermissionError("create"));
      return;
    }
    startCreate();
    setMessage(null);
  };

  const handleEditClick = async (organization) => {
    // Set initial form state immediately
    startEdit(organization);
    setMessage(null);

    // Fetch and update settings
    const settingsResult = await fetchOrganizationSettings(organization.orgId);
    if (settingsResult.success) {
      startEdit(organization, settingsResult.settings);
      logger.log(
        "[OrganizationManagement] Settings loaded:",
        settingsResult.settings
      );
    }
  };

  const handleCreateSubmit = async (formDataToSubmit) => {
    if (!permissions.canCreateOrganization) {
      showMessage("error", permissions.getPermissionError("create"));
      return;
    }

    // Validate form
    const validationError = validateForm(formDataToSubmit.name);
    if (validationError) {
      showMessage("error", validationError);
      return;
    }

    const result = await createOrganization(formDataToSubmit.name);
    if (result.success) {
      resetForm();
    }
  };

  const handleUpdateSubmit = async (formDataToSubmit) => {
    // Validate form
    const validationError = validateForm(
      formDataToSubmit.name,
      editingOrganization.orgId
    );
    if (validationError) {
      showMessage("error", validationError);
      return;
    }

    setLoading(true);
    setMessage(null);

    const settings = {
      autoProvisionCredentials: formDataToSubmit.autoProvisionCredentials,
      receiveCredentialNotifications:
        formDataToSubmit.receiveCredentialNotifications,
      enableDevStreamsForAllRoles: formDataToSubmit.enableDevStreamsForAllRoles,
    };

    const result = await updateOrganization(
      editingOrganization.orgId,
      formDataToSubmit.name,
      settings
    );

    if (result.success) {
      resetForm();
    } else {
      showMessage("error", result.error || "Failed to update organization");
    }

    setLoading(false);
  };

  const handleDelete = async (organization) => {
    const installationCount = organization.installations?.length || 0;

    // Show confirmation modal
    setDeleteConfirmation({ organization, installationCount });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const { organization } = deleteConfirmation;

    setLoading(true);
    setMessage(null);
    setDeleteConfirmation(null); // Close confirmation modal

    await deleteOrganization(
      organization.orgId,
      organization.name,
      currentOrganizationId
    );

    setLoading(false);
  };

  const handleSwitchToNewOrganization = (shouldSwitch) => {
    if (shouldSwitch && pendingSwitch && onSwitchOrganization) {
      // Use the context method to switch organization
      const targetOrganization = organizations.find(
        (o) => o.orgId === pendingSwitch.orgId
      );
      if (targetOrganization) {
        onSwitchOrganization(targetOrganization);
        // Close the main modal and show installation selector
        onClose();
      }
    }
    // Don't close modal if user chose "Not Now"
    setPendingSwitch(null);
  };

  const handleAuthorize = async (organization) => {
    logger.log(
      "[handleAuthorize] Authorizing organization:",
      organization.orgId
    );
    setMessage(null);
    await authorizeOrganization(organization.orgId);
  };

  const handleUnauthorizeClick = (organization) => {
    // Only Full Admins can unauthorize
    if (!permissions.canUnauthorizeOrganization) {
      showMessage("error", permissions.getPermissionError("unauthorize"));
      return;
    }

    // Show confirmation modal
    setDeauthorizeConfirmation({ organization });
  };

  const handleUnauthorizeConfirm = async () => {
    if (!deauthorizeConfirmation) return;

    const organization = deauthorizeConfirmation.organization;
    setDeauthorizeConfirmation(null);
    setMessage(null);

    await unauthorizeOrganization(organization.orgId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  if (!isOpen) return null;

  const hasOrganizations = organizations && organizations.length > 0;

  return (
    <div
      className={`organization-management-modal-overlay ${theme}`}
      onClick={handleClose}
    >
      <div
        className={`organization-management-modal ${theme}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Organization Management</h2>
          <button
            className="close-x-button"
            onClick={handleClose}
            disabled={loading}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Message Display */}
          {message && (
            <div className={`message-banner ${message.type}`}>
              {message.type === "info" && (
                <div className="loading-spinner"></div>
              )}
              {message.text}
            </div>
          )}

          {/* Create/Edit Form */}
          {(() => {
            if (showCreateForm || editingOrganization) {
              return (
                <OrganizationForm
                  mode={editingOrganization ? "edit" : "create"}
                  formData={formData}
                  onFormDataChange={setFormData}
                  onSubmit={
                    editingOrganization
                      ? handleUpdateSubmit
                      : handleCreateSubmit
                  }
                  onCancel={handleCancelForm}
                  loading={loading}
                  userProfile={userProfile}
                />
              );
            }
            return null;
          })()}

          {/* Organization List */}
          {(() => {
            if (!showCreateForm && !editingOrganization) {
              return (
                <>
                  <div className="organizations-header">
                    <h3>Organizations</h3>
                    {permissions.canCreateOrganization && (
                      <button
                        className="btn-create"
                        onClick={handleCreateClick}
                        disabled={loading}
                      >
                        + Create Organization
                      </button>
                    )}
                  </div>

                  {/* Search Box */}
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search organizations by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="search-clear-button"
                        onClick={() => setSearchTerm("")}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {!hasOrganizations ? (
                    <div className="empty-state">
                      <p>No organizations found.</p>
                    </div>
                  ) : (
                    <OrganizationTable
                      organizations={organizations}
                      searchTerm={searchTerm}
                      currentOrganizationId={currentOrganizationId}
                      loading={loading}
                      copiedOrgId={copiedOrgId}
                      userProfile={userProfile}
                      onSwitchOrganization={onSwitchOrganization}
                      onViewUsers={(org) => {
                        setSelectedOrgForUsers(org);
                        setShowOrgUsersModal(true);
                      }}
                      onCopyOrgId={handleCopyOrgId}
                      onOpenInstallationManagement={
                        onOpenInstallationManagement
                      }
                      onEdit={handleEditClick}
                      onAuthorize={handleAuthorize}
                      onUnauthorize={handleUnauthorizeClick}
                      onDelete={handleDelete}
                      formatDate={formatDate}
                    />
                  )}
                </>
              );
            }
            return null;
          })()}
        </div>

        {/* Modal Footer with Cancel Button */}
        <div className="modal-actions">
          <button
            className="cancel-button"
            onClick={handleClose}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>

      {/* Deauthorize Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!deauthorizeConfirmation}
        title="Confirm Deauthorization"
        message={
          <>
            Are you sure you want to deauthorize{" "}
            <strong>{deauthorizeConfirmation?.organization.name}</strong> from
            the app?
          </>
        }
        warningMessage="This will effectively disable visibility to app events (web hooks will no longer be received) and some functionality such as granting organization access to other members will break because the app (M2M) tokens will no longer have required authorization to complete these activities."
        noteMessage="To confirm, type the organization name exactly as shown:"
        confirmText="Deauthorize"
        confirmationTarget={deauthorizeConfirmation?.organization.name}
        requiresTyping={true}
        loading={loading}
        onConfirm={handleUnauthorizeConfirm}
        onCancel={() => {
          setDeauthorizeConfirmation(null);
        }}
      />

      {/* Switch Organization Modal */}
      {pendingSwitch && (
        <div className="switch-organization-overlay" onClick={handleClose}>
          <div
            className="switch-organization-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="switch-organization-header">
              <h3>Switch Organization</h3>
            </div>
            <div className="switch-organization-body">
              <p>
                Organization <strong>"{pendingSwitch.name}"</strong> has been
                created successfully!
              </p>
              <p>Would you like to switch to this organization now?</p>
            </div>
            <div className="switch-organization-actions">
              <button
                className="cancel-button"
                onClick={() => handleSwitchToNewOrganization(false)}
                disabled={loading}
              >
                Not Now
              </button>
              <button
                className="switch-confirm-button"
                onClick={() => handleSwitchToNewOrganization(true)}
                disabled={loading}
              >
                Switch Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!deleteConfirmation}
        title="Confirm Deletion"
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>"{deleteConfirmation?.organization.name}"</strong>?
          </>
        }
        noteMessage="This action cannot be undone."
        confirmText="Delete"
        confirmationTarget={deleteConfirmation?.organization.name}
        requiresTyping={deleteConfirmation?.installationCount === 0}
        disabled={deleteConfirmation?.installationCount > 0}
        loading={loading}
        onConfirm={confirmDelete}
        onCancel={() => {
          logger.log("[Delete Confirmation] Cancel button clicked");
          setDeleteConfirmation(null);
        }}
        renderExtraContent={() =>
          deleteConfirmation?.installationCount > 0 ? (
            <div
              className="confirmation-dialog-warning"
              style={{ marginTop: "16px", marginBottom: "0" }}
            >
              ⚠️ This organization has {deleteConfirmation.installationCount}{" "}
              {deleteConfirmation.installationCount === 1
                ? "site"
                : "sites"}
              . You must remove all sites before deleting.
            </div>
          ) : null
        }
      />

      {/* Authorization Status Modal */}
      {/* Organization Authorization Modal */}
      <StatusModal
        isOpen={authorizationModal.isOpen}
        isLoading={authorizationModal.isLoading}
        result={authorizationModal.result}
        loadingMessage="Processing authorization request..."
        errorButtonText="Close"
        onClose={() => {
          setAuthorizationModal({
            isOpen: false,
            isLoading: false,
            result: null,
          });
        }}
      />

      {/* Organization Creation Modal */}
      <StatusModal
        isOpen={creationModal.isOpen}
        isLoading={creationModal.isLoading}
        result={creationModal.result}
        loadingMessage="Creating organization..."
        onClose={() => {
          setCreationModal({
            isOpen: false,
            isLoading: false,
            result: null,
          });
        }}
        onSuccess={(result) => {
          // After closing creation modal, show switch prompt if successful
          if (result.organization) {
            setPendingSwitch({
              orgId: result.organization.orgId,
              name: result.organization.name,
            });
          }
        }}
      />

      {/* Organization Update Modal */}
      <StatusModal
        isOpen={updateModal.isOpen}
        isLoading={updateModal.isLoading}
        result={updateModal.result}
        loadingMessage="Updating organization settings..."
        errorButtonText="Close"
        onClose={() => {
          setUpdateModal({
            isOpen: false,
            isLoading: false,
            result: null,
          });
        }}
      />

      {/* Organization Deletion Modal */}
      <StatusModal
        isOpen={deletionModal.isOpen}
        isLoading={deletionModal.isLoading}
        isRefreshing={deletionModal.isRefreshing}
        result={deletionModal.result}
        loadingMessage="Deleting organization..."
        refreshingMessage="Loading organizations..."
        successButtonText={
          deletionModal.result?.isDeletingCurrent ? "Continue" : "Close"
        }
        errorButtonText="Close"
        onClose={() => {
          // If deleted current organization, close modal and trigger change organization
          if (deletionModal.result?.isDeletingCurrent) {
            setDeletionModal({
              isOpen: false,
              isLoading: false,
              isRefreshing: false,
              result: null,
              isDeletingCurrent: false,
            });
            onClose();
            if (onChangeOrganization) {
              onChangeOrganization(true); // Pass true to indicate forced change (deleted current)
            }
          } else {
            setDeletionModal({
              isOpen: false,
              isLoading: false,
              isRefreshing: false,
              result: null,
              isDeletingCurrent: false,
            });
          }
        }}
        renderExtraContent={(result) =>
          result.isDeletingCurrent && result.type === "success" ? (
            <p className="deletion-submessage">
              You will be redirected to select a new organization.
            </p>
          ) : null
        }
      />

      {/* Organization Users Modal */}
      {showOrgUsersModal && selectedOrgForUsers && (
        <OrgUsersModal
          organization={selectedOrgForUsers}
          onClose={() => {
            setShowOrgUsersModal(false);
            setSelectedOrgForUsers(null);
          }}
          onUserRevoked={() => {
            // Optionally refresh org data
            if (onOrganizationsChanged) {
              onOrganizationsChanged();
            }
          }}
        />
      )}
    </div>
  );
};

export default OrganizationManagementModal;
